import React from "react";
import { Typography, Tooltip } from "@material-ui/core";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import Badge, { BadgeTypes } from "../Badge/Badge";
import { authorizationTagLabels, authorizationTagVariant, getInitials } from "./utils/getInitials";
import { formatGuardiaFecha } from "./utils/formatDate";
import { formatManualAuthorizer, getTransportAuthorizer } from "./utils/getTransportAuthorizer";
import type { GuardiaRow } from "./GuardiaTable";

/**
 * Celdas compartidas entre la vista "por persona" (GuardiaTable) y la vista "por vehículo"
 * (GuardiaVehicleTable). Mismo markup y mismas clases CSS que tenía GuardiaTable inline.
 */

export const FechaCell: React.FC<{ fecha: string }> = ({ fecha }) => {
  const { day, time } = formatGuardiaFecha(fecha);
  return (
    <div className="gt-fecha-cell">
      <span className="gt-fecha-day">{day}</span>
      <span className="gt-fecha-time">{time}</span>
    </div>
  );
};

export const PersonaCell: React.FC<{ row: GuardiaRow; showAllTags?: boolean }> = ({
  row,
  showAllTags,
}) => {
  const authorizationTags = row.event?.authorizationTags ?? [];
  // El recorte existe para la columna angosta de la vista por persona; donde la celda ocupa el
  // ancho de la fila entera no hace falta esconder nada detrás de un "+N". Dos entran siempre:
  // la columna nunca baja de ~210px, que es el 28% del ancho mínimo de la tabla.
  const visibleTagsCount = showAllTags ? authorizationTags.length : 2;
  const visibleTags = authorizationTags.slice(0, visibleTagsCount);
  const hiddenTags = authorizationTags.slice(visibleTagsCount);

  return (
    <div className="gt-persona-cell">
      <div className="gt-persona-top">
        <div className="gt-avatar">{getInitials(row.nombre, "")}</div>
        <div className="gt-persona-info">
          <span className="gt-persona-nombre">{row.nombre}</span>
          <span className="gt-persona-dni">DNI: {row.dni}</span>
        </div>
      </div>
      {authorizationTags.length > 0 && (
        <div className="gt-tags-container">
          {visibleTags.map((tag, idx) => (
            <Badge
              key={idx}
              label={authorizationTagLabels[tag] || tag}
              // "grey" y no "default": el fallback tiene que ser un BadgeTypes real, si no el
              // badge se queda sin fondo ni borde y se ve como texto suelto.
              variant={(authorizationTagVariant[tag] as BadgeTypes) ?? "grey"}
            />
          ))}
          {hiddenTags.length > 0 && (
            <Tooltip
              title={
                <div className="gt-tags-tooltip">
                  {hiddenTags.map((tag, idx) => (
                    <span key={idx}>{authorizationTagLabels[tag] || tag}</span>
                  ))}
                </div>
              }
              placement="top"
              arrow
            >
              <span className="gt-tags-overflow">+{hiddenTags.length}</span>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export const HostCell: React.FC<{ row?: GuardiaRow }> = ({ row }) => {
  if (!row) return <Typography>—</Typography>;
  // Un anfitrión de verdad siempre manda: el texto libre sólo aparece cuando no hay ninguno.
  const authorizer = row.host ? undefined : getTransportAuthorizer(row);
  return (
    <div className="gt-host-cell">
      {row.empresa && <span className="gt-host-empresa-bold">{row.empresa}</span>}
      {row.host ? (
        <span className="gt-host-nombre">
          {[row.host.firstname, row.host.lastname].filter(Boolean).join(" ") || "—"}
        </span>
      ) : authorizer ? (
        <span className="gt-host-nombre">{formatManualAuthorizer(authorizer)}</span>
      ) : (
        !row.empresa && <Typography>—</Typography>
      )}
    </div>
  );
};

/** Botón de salida de las tablas de personas y de unidades: mismo control en las dos. */
export const ExitButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <button type="button" className="gt-btn-salida" onClick={onClick}>
    <ExitToAppIcon fontSize="small" />
    Salida
  </button>
);
